import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LocaleLink } from '@/components/LocaleLink'
import { CircularFlag } from '@/components/ui/circular-flag'
import { LocaleSettingsModal } from '@/components/layout/LocaleSettingsModal'
import { useLocalePreferenceStore } from '@/stores/locale-preference'
import { REGIONS } from '@/lib/regions'
import { CURRENCIES } from '@/lib/currencies'

export function Footer() {
  const { t, i18n } = useTranslation('navigation')
  const year = new Date().getFullYear()
  const { region, currency } = useLocalePreferenceStore()
  const [localeModalOpen, setLocaleModalOpen] = useState(false)

  const regionEntry = REGIONS.find((r) => r.code.toUpperCase() === region)
  const regionLabel = regionEntry ? t(regionEntry.nameKey) : region
  const languageLabel = t(`chrome.locale.languages.${i18n.language}`)
  const currencyEntry = CURRENCIES.find((c) => c.code === currency)
  const currencyLabel = currencyEntry ? t(currencyEntry.nameKey) : currency

  return (
    <footer className="flex flex-col items-center justify-center gap-4 md:gap-8 py-6 mt-4 border-t border-slate-200 dark:border-slate-800/60 md:flex-row">
      <button
        type="button"
        onClick={() => setLocaleModalOpen(true)}
        aria-label={t('chrome.locale.triggerLabel', {
          region: regionLabel,
          language: languageLabel,
          currency: currencyLabel,
        })}
        className="flex items-center gap-2 rounded-full px-3 min-h-[44px] text-[11px] text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/60 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
      >
        {regionEntry && <CircularFlag regionCode={regionEntry.code} />}
        <span>{regionLabel}</span>
        <span aria-hidden>|</span>
        <span>{languageLabel}</span>
        <span aria-hidden>|</span>
        <span>{currencyLabel}</span>
      </button>

      <div className="flex flex-row items-center gap-1.5">
        <p className="text-[11px] text-slate-400 dark:text-slate-500 font-semibold">
          {t('chrome.footer.copyright', { year })}
        </p>
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 px-4">
          <LocaleLink
            to="/terms"
            className="text-[11px] text-slate-400 dark:text-slate-500 hover:text-ocean-600 dark:hover:text-ocean-400 transition-colors"
          >
            {t('chrome.footer.terms')}
          </LocaleLink>
          <span className="text-[11px] text-slate-300 dark:text-slate-700" aria-hidden>·</span>
          <LocaleLink
            to="/privacy"
            className="text-[11px] text-slate-400 dark:text-slate-500 hover:text-ocean-600 dark:hover:text-ocean-400 transition-colors"
          >
            {t('chrome.footer.privacy')}
          </LocaleLink>
          <span className="text-[11px] text-slate-300 dark:text-slate-700" aria-hidden>·</span>
          <LocaleLink
            to="/accessibility"
            className="text-[11px] text-slate-400 dark:text-slate-500 hover:text-ocean-600 dark:hover:text-ocean-400 transition-colors"
          >
            {t('chrome.footer.accessibility')}
          </LocaleLink>
          <span className="text-[11px] text-slate-300 dark:text-slate-700" aria-hidden>·</span>
          <LocaleLink
            to="/privacy#your-rights"
            className="text-[11px] text-slate-400 dark:text-slate-500 hover:text-ocean-600 dark:hover:text-ocean-400 transition-colors"
          >
            {t('chrome.footer.doNotSell')}
          </LocaleLink>
        </div>
      </div>

      <LocaleSettingsModal open={localeModalOpen} onOpenChange={setLocaleModalOpen} />
    </footer>
  )
}
